// ABOUTME: SPEC-52 — the waitlist modal's own state: when it is open, what it holds, what it
// ABOUTME: sends. One `useReducer` (D-13/AC-38), composed into App.js as a single hook call.
//
// Two ways in (D-14 requires both end at the same `close`):
//   - `openFromAdd(item)` — the manual Add-to-Bag guard in App.js's `handleAddToCart`, for a
//     product the envelope (or the reel_spec_sheet payload) already marked `waitlist: true`.
//   - `handleServerMessage(data)` — the agent's `open_waitlist_form`/`dismiss_waitlist_form` tools,
//     which arrive as a `ui_update` action `waitlist_form` (App.js routes it in, see the message
//     chain beside `prompt_upload`), and the same channel carries the post-submit `state: 'confirmed'`
//     transition (D-10) — the modal does not decide for itself that a submit succeeded.
//
// Never transcribes an email (D-9): the four fields are only ever set by `setField`, called from a
// real `<input onChange>` in WaitlistFields.js.

import { useCallback, useEffect, useReducer, useRef } from 'react';
import { CONFIRM_DWELL_MS, SUBMIT_TIMEOUT_MS } from './waitlistGeometry';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const EMPTY_FIELDS = { name: '', email: '', phone: '', message: '' };

const initialState = {
  open: false,
  state: 'form', // 'form' | 'submitting' | 'confirmed' | 'error'  (VER-89 added `error`)
  collection: '',
  variants: [],   // [{id, title}], chip candidates
  selected: [],   // ticked variant ids
  fields: EMPTY_FIELDS,
  errors: {},
  joined: {},     // SPEC-52 D-8 domain hydration: {variant_id: iso8601}, informational
  pending: [],
};

// Chips arriving from the agent's `open_waitlist_form(variants)` are ids only (the tool's own
// signature, §4.7) — no title travelled with them, so the chip falls back to the id itself rather
// than inventing one. A manual open (openFromAdd) always has a title, from the product it guarded.
function normalizeVariants(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((v) => (typeof v === 'string' ? { id: v, title: v } : { id: v?.id, title: v?.title || v?.id }))
    .filter((v) => v.id);
}

function reducer(state, action) {
  switch (action.type) {
    case 'OPEN_FROM_ADD': {
      const variants = action.item ? [{ id: action.item.id, title: action.item.title || action.item.id }] : [];
      return {
        ...initialState, joined: state.joined, pending: state.pending,
        open: true, variants, selected: variants.map((v) => v.id), collection: state.collection,
      };
    }
    case 'SERVER_MESSAGE': {
      const data = action.data || {};
      if (data.open === false) return { ...initialState, joined: state.joined, pending: state.pending, collection: state.collection };
      // Guard (review finding, 2026-09-10): a submit's confirm can arrive after the guest already
      // dismissed the panel (Escape/X during `submitting`) — without this check that late message
      // reopens a panel the guest closed. Only a submit actually in flight may be confirmed; any
      // other confirm arriving is a no-op.
      if (data.state === 'confirmed') {
        return state.state === 'submitting' ? { ...state, open: true, state: 'confirmed' } : state;
      }
      const variants = normalizeVariants(data.variants);
      return {
        ...initialState, joined: state.joined, pending: state.pending,
        open: true, variants, selected: variants.map((v) => v.id),
        collection: data.collection || state.collection,
      };
    }
    case 'CLOSE':
      return { ...initialState, joined: state.joined, pending: state.pending, collection: state.collection };
    case 'SET_FIELD':
      return { ...state, fields: { ...state.fields, [action.key]: action.value }, errors: { ...state.errors, [action.key]: undefined } };
    case 'TOGGLE_VARIANT': {
      const has = state.selected.includes(action.id);
      return { ...state, selected: has ? state.selected.filter((id) => id !== action.id) : [...state.selected, action.id] };
    }
    case 'SET_ERRORS':
      return { ...state, errors: action.errors };
    case 'SUBMITTING':
      return { ...state, state: 'submitting' };
    // VER-89 — the fields are deliberately preserved: the guest typed them, the failure was ours.
    case 'SUBMIT_FAILED':
      return state.state === 'submitting' ? { ...state, state: 'error' } : state;
    case 'HYDRATE':
      return { ...state, joined: action.value?.joined || {}, pending: Array.isArray(action.value?.pending) ? action.value.pending : [] };
    default:
      return state;
  }
}

function validate(fields) {
  const errors = {};
  if (!fields.name.trim()) errors.name = true;
  if (!EMAIL_RE.test(fields.email.trim())) errors.email = true;
  return errors;
}

/** @param {{ sendMessage?: (msg: object) => void, collection?: string, modalCopy?: object }} config */
export function useWaitlist({ sendMessage, collection = '', modalCopy = null } = {}) {
  // `collection` is deliberately NOT seeded into reducer state here (see the hook's return) — a
  // `useReducer` initial value is captured once, at mount, before the async `/api/config` prop
  // has necessarily arrived.
  const [state, dispatch] = useReducer(reducer, initialState);

  // Auto-close the confirmation (D-13 §4.12): nothing left to cancel once it's up.
  useEffect(() => {
    if (state.state !== 'confirmed') return undefined;
    const t = setTimeout(() => dispatch({ type: 'CLOSE' }), CONFIRM_DWELL_MS);
    return () => clearTimeout(t);
  }, [state.state]);

  // VER-89: a submit the server never answers. The cleanup clears the timer the moment the state
  // leaves `submitting`, so a confirm that arrives first wins and the timer cannot fire behind it.
  useEffect(() => {
    if (state.state !== 'submitting') return undefined;
    const t = setTimeout(() => dispatch({ type: 'SUBMIT_FAILED' }), SUBMIT_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, [state.state]);

  // Prod 2026-10-14: the same per-panel "closed" signal App.js's overlay effect sends for the
  // cart/wishlist/privacy panels. Without it a panel the agent opened and the guest closed by hand
  // stayed "already open" server-side, so the next voice ask opened nothing. Fires on the
  // open→closed transition only; a close the server itself ordered is a harmless no-op there.
  const wasOpenRef = useRef(state.open);
  useEffect(() => {
    if (wasOpenRef.current && !state.open) {
      sendMessage?.({ type: 'interaction', action: 'closed waitlist panel' });
    }
    wasOpenRef.current = state.open;
  }, [state.open, sendMessage]);

  const openFromAdd = useCallback((item) => {
    dispatch({ type: 'OPEN_FROM_ADD', item });
    // Owner requirement, 2026-09-10: tell the backend a MANUAL click opened the panel, so the
    // agent can acknowledge it the same way it already does for a voice-triggered open. The voice
    // path already tells the backend via the `open_waitlist_form` TOOL CALL itself — this
    // notification exists only because `openFromAdd` is the one way in that is otherwise entirely
    // client-side (R3: a manual Add-to-Bag never reaches `add_to_cart` at all). Reuses the
    // `waitlist_form` message shape rather than inventing a new type — `action` distinguishes this
    // from a future client-originated case the same way it already does server-side (open/confirm).
    sendMessage?.({ type: 'waitlist_form', action: 'opened_manual', product_id: item?.id ?? null });
  }, [sendMessage]);
  const handleServerMessage = useCallback((data) => dispatch({ type: 'SERVER_MESSAGE', data }), []);
  const hydrate = useCallback((value) => dispatch({ type: 'HYDRATE', value }), []);
  const close = useCallback(() => dispatch({ type: 'CLOSE' }), []);
  const setField = useCallback((key, value) => dispatch({ type: 'SET_FIELD', key, value }), []);
  const toggleVariant = useCallback((id) => dispatch({ type: 'TOGGLE_VARIANT', id }), []);

  const submit = useCallback(() => {
    const errors = validate(state.fields);
    if (Object.keys(errors).length) { dispatch({ type: 'SET_ERRORS', errors }); return; }
    dispatch({ type: 'SUBMITTING' });
    const sent = sendMessage?.({
      type: 'waitlist_submit',
      name: state.fields.name.trim(),
      email: state.fields.email.trim(),
      phone: state.fields.phone.trim(),
      message: state.fields.message.trim(),
      variants: state.selected,
    });
    // `App.js`'s sendMessage no-ops when the socket is not OPEN. It now reports that, so the
    // panel can say so immediately instead of waiting out the timeout. `undefined` means a caller
    // that predates the boolean — treat it as sent, never as failed.
    if (sent === false) dispatch({ type: 'SUBMIT_FAILED' });
  }, [state.fields, state.selected, sendMessage]);

  return {
    open: state.open, state: state.state,
    // Review finding HIGH 3 (2026-09-10): NOT frozen from the reducer's initial state, which
    // captures `collection` once at mount — before `brandConfig` (and so this `collection` prop)
    // has loaded from `/api/config`. Falling back to the live prop on every render means the
    // eyebrow renders correctly however late the config arrives; a server message's own
    // `collection` (state.collection) still wins once one has arrived.
    collection: state.collection || collection,
    variants: state.variants, selected: state.selected, fields: state.fields, errors: state.errors,
    joined: state.joined, pending: state.pending, modalCopy,
    openFromAdd, handleServerMessage, hydrate, close, setField, toggleVariant, submit,
  };
}
